import { ReactNode, useEffect, useId, useRef } from 'react';
import { cn } from '../../../utils/cn';
import { Icon } from '../Icon/Icon';

/** Visual box shared by Checkbox and MultiSelect options. */
export function CheckMark({ checked, indeterminate, className }: { checked?: boolean; indeterminate?: boolean; className?: string }) {
  const on = checked || indeterminate;
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px] transition-colors',
        on ? 'border-brand-600 bg-brand-600 text-white' : 'border-navy-300 bg-white',
        className,
      )}
    >
      {indeterminate ? <Icon name="minus" size={14} strokeWidth={3} /> : checked && <Icon name="check" size={14} strokeWidth={3} />}
    </span>
  );
}

export interface CheckboxProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  indeterminate?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  error?: ReactNode;
}

export function Checkbox({ checked = false, onChange, label, description, indeterminate = false, disabled, className, id, name, error }: CheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);
  const autoId = useId();
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <div className={className}>
      <label
        htmlFor={id || autoId}
        className={cn('group inline-flex select-none items-start gap-3', disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}
      >
        <span className="relative mt-[3px] inline-flex">
          <input
            ref={ref}
            id={id || autoId}
            name={name}
            type="checkbox"
            className="peer sr-only"
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.checked)}
          />
          <CheckMark
            checked={checked}
            indeterminate={indeterminate}
            className={cn(
              'peer-focus-visible:ring-2 peer-focus-visible:ring-steel peer-focus-visible:ring-offset-2',
              !checked && !indeterminate && !disabled && 'group-hover:border-brand-500',
              error && !checked && 'border-red-500',
            )}
          />
        </span>
        {(label || description) && (
          <span className="flex flex-col">
            {label && <span className="text-sm text-navy-900">{label}</span>}
            {description && <span className="text-[13px] leading-snug text-ink-400">{description}</span>}
          </span>
        )}
      </label>
      {error && <p className="mt-1 pl-8 text-[13px] text-red-600">{error}</p>}
    </div>
  );
}

/** On/off toggle for settings like "Active". */
export function Switch({ checked = false, onChange, label, description, disabled, className }: Omit<CheckboxProps, 'indeterminate' | 'id' | 'name' | 'error'>) {
  const id = useId();
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      {(label || description) && (
        <label htmlFor={id} className="flex flex-col">
          <span className="text-sm text-navy-900">{label}</span>
          {description && <span className="text-[13px] text-ink-400">{description}</span>}
        </label>
      )}
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange?.(!checked)}
        className={cn(
          'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-steel focus-visible:ring-offset-2 disabled:opacity-50',
          checked ? 'bg-brand-600' : 'bg-navy-200',
        )}
      >
        <span
          className={cn(
            'inline-block h-5 w-5 rounded-full shadow transition-transform',
            checked ? 'translate-x-6 bg-white' : 'translate-x-1 bg-white',
          )}
        />
      </button>
    </div>
  );
}

export default Checkbox;
